/**
 * Admin — Revenue Calculation, the reconciliation panel (card 1245, extended by card 2260).
 *
 * The page's own self-test, in TWO blocks because there are two different questions:
 *   1. STORED ECONOMICS (card 2260) — the five surfaces publishing the versioned
 *      `order_economics` money lines. Rendered by `EconomicsReconciliationPanel`.
 *   2. LEGACY COLUMNS — the three surfaces still totalling the per-order `orders.*_cost`
 *      columns, below.
 *
 * ⛔ A PANEL THAT CANNOT FAIL CANNOT PASS. Deltas are exact cents from the API and are never
 *   rounded toward agreement in the view: `PASS` is rendered only where the API reports a zero
 *   delta AND no structural defect.
 */

import { formatPrice } from '../../utils/formatters';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';
import EconomicsReconciliation, { DeltaCell } from './EconomicsReconciliationPanel';

/** Pure. One legacy surface row. */
export const ReconciliationRow = ({ row }) => (
  <li
    className={row.status === 'PASS' ? styles.reconRowPass : styles.reconRowFail}
    data-testid={`recon-row-${row.key}`}
    data-status={row.status}
  >
    <div className={styles.reconHead}>
      <span
        className={row.status === 'PASS' ? styles.reconBadgePass : styles.reconBadgeFail}
        data-testid={`recon-status-${row.key}`}
      >
        {row.status}
      </span>
      <span className={styles.reconLabel}>{row.label}</span>
      <span className={styles.reconPopulation} data-testid={`recon-population-${row.key}`}>
        {row.orderCount} order{row.orderCount === 1 ? '' : 's'}
        {row.orderCountDelta === 0 ? '' : ` (${row.orderCountDelta > 0 ? '+' : ''}${row.orderCountDelta})`}
      </span>
    </div>

    <dl className={styles.reconGrid}>
      <dt className={styles.reconTerm}>Predicate</dt>
      <dd className={styles.reconDef} data-testid={`recon-predicate-${row.key}`}>{row.predicate}</dd>

      <dt className={styles.reconTerm}>Arithmetic</dt>
      <dd className={styles.reconDef}>{row.arithmetic}</dd>

      <dt className={styles.reconTerm}>Source</dt>
      <dd className={styles.reconDefMono}>{row.source}</dd>

      <dt className={styles.reconTerm}>Gross revenue</dt>
      <dd className={styles.reconDef}>
        {formatPrice(row.grossRevenueCents)} · delta <DeltaCell cents={row.deltaGrossCents} />
      </dd>

      <dt className={styles.reconTerm}>Total cost</dt>
      <dd className={styles.reconDef}>
        {formatPrice(row.totalCostCents)} · delta <DeltaCell cents={row.deltaCostCents} />
      </dd>
    </dl>

    {row.deltaNote && (
      <p className={styles.reconDeltaNote} data-testid={`recon-note-${row.key}`}>{row.deltaNote}</p>
    )}

    {(row.causes ?? []).length > 0 && (
      <ul className={styles.reconCauses}>
        {row.causes.map((cause) => (
          <li key={cause} className={styles.reconCause}>{cause}</li>
        ))}
      </ul>
    )}
  </li>
);

/** Pure. The panel. */
const RevenueReconciliationPanel = ({ reconciliation }) => {
  const {
    surfaces = [], knownMismatches = [], resolvedMismatches = [],
    passCount = 0, failCount = 0, canonical, economics = null
  } = reconciliation ?? {};

  return (
    <div className={styles.recon} data-testid="reconciliation-panel">
      {economics && <EconomicsReconciliation economics={economics} />}

      <h3 className={styles.reconGroupTitle}>Legacy order-cost columns</h3>
      <p
        className={failCount > 0 ? styles.reconVerdictFail : styles.reconVerdictPass}
        data-testid="recon-verdict"
      >
        {failCount === 0
          ? `All ${passCount} surfaces agree to the cent.`
          : `${passCount} of ${passCount + failCount} surfaces agree to the cent. ${failCount} ${failCount === 1 ? 'does' : 'do'} not.`}
      </p>

      <p className={styles.reconLead}>
        Graded against this page: <strong>{canonical?.orderCount ?? 0}</strong> orders,{' '}
        {formatPrice(canonical?.grossCents ?? 0)} gross, {formatPrice(canonical?.costCents ?? 0)} cost.
        Every delta below is exact cents — a mismatch is reported with its named cause, never
        rounded into agreement.
      </p>

      <ul className={styles.reconList} data-testid="recon-surfaces">
        {surfaces.map((row) => <ReconciliationRow key={row.key} row={row} />)}
      </ul>

      <h3 className={styles.reconGroupTitle}>Known live mismatches</h3>
      {knownMismatches.length === 0 ? (
        <div data-testid="recon-no-known-mismatches">
          <p className={styles.reconGroupNote}>
            None. Both defects this panel used to name have been fixed and were re-measured against live
            code before being removed — a panel that keeps describing a repaired defect is a stale
            document, not a self-test.
          </p>
          {resolvedMismatches.length > 0 && (
            <ul className={styles.reconCauses}>
              {resolvedMismatches.map((item) => (
                <li key={item} className={styles.reconCause}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <p className={styles.reconGroupNote}>
            Named defects that exist in the codebase today. They are graded FAIL on the structure of
            their predicate, so they stay visible even in a window where their dollar delta is $0.00.
          </p>
          <ul className={styles.reconList} data-testid="recon-known-mismatches">
            {knownMismatches.map((row) => <ReconciliationRow key={row.key} row={row} />)}
          </ul>
        </>
      )}
    </div>
  );
};

export default RevenueReconciliationPanel;
